import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import ThemeButtonFloating from 'assets/components/button/themes/Theme.Button.Floating';
import Card from 'assets/components/card/Card';
import ThemeCardRow from 'assets/components/card/themes/Theme.Card.Row';
import Icon from 'assets/components/icon/Icon';
import Page from 'assets/components/page/Page';
import ThemePageList from 'assets/components/page/themes/Theme.Page.List';
import Pagination from 'assets/components/pagination/Pagination';
import ThemePaginationRounded from 'assets/components/pagination/themes/Theme.Pagination.Rounded';
import { getLocales } from 'assets/locales/Locale';
import { productManagementHomeRoute } from 'config/Api.Config';
import { isMobile } from 'react-device-detect';
import Confirm from 'assets/components/page/templates/Confirm';
import ThemeConfirmDefault from 'assets/components/page/templates/themes/Theme.Confirm.Default';
import { replaceStringTemplateWithValues } from 'assets/utils/data/String';
import Theme from 'assets/themes/Theme.Common';
import ThemeButtonTab from 'assets/components/button/themes/Theme.Button.Tab';
import {
  ProductDealerGroupDocumentsAction,
  useProductDealerGroupDocumentProvider,
} from './ProductDocument.DealerGroupDocuments.Hooks';
import { ProductDocumentTabs, useProductDocumentProvider } from '../ProductDocument.Hooks';
import ProductDealerGroupDocumentAdd from './sections/ProductDocument.DealerGroupDocuments.Add';
import DealerGroupDocumentList from './sections/ProductDocument.DealerGroupDocuments.List';
import ProductDealerGroupDocumentFilters from './sections/ProductDocument.DealerGroupDocuments.Filters';
import Box from 'assets/components/box/Box';
import UploadDocumentTranslationButton from '../../../components/translations/UploadDocumentTranslationButton';

enum Actions {
  ADD = 'add',
  DELETE = 'delete',
}
export type Verify = Utils.VerifyExtends<
  Module.ProductManagement.ProductDocument.Actions,
  Utils.ValueOf<typeof Actions>
>;
export const productDocumentIndexRoute = productManagementHomeRoute + '/productDocument/:id?';
export default function ProductDealerGroupDocumentIndex() {
  const { lang } = getLocales();
  const productDocumentHook = useProductDocumentProvider();
  const {
    currentView,
    dealerGroupDocumentList,
    setView,
    selectedItems,
    translatedDocumentList,
    selectedTranslatedItems,
    toggleSelectAllTranslations,
    views,
    handleDeleteDealerGroupDocumentSubmit,
    toggleSelectAll,
  } = useProductDealerGroupDocumentProvider();

  return (
    <>
      <Page
        class={ThemePageList}
        isLoading={productDocumentHook.isLoadingProductDocument}
        style={{
          pageHeader: {
            flexDirection: 'column',
            alignItems: 'flex-start',
            paddingBottom: 0,
            background: Theme.Color.primary,
            color: Theme.Color.primaryAlt,
            borderBottom: 'none',
          },
        }}
        header={
          <>
            <Box style={{ display: 'flex', width: '100%', alignItems: 'center' }}>
              <Card
                class={ThemeCardRow}
                style={{
                  cardMedia: {
                    color: Theme.Color.primary,
                    background: Theme.Color.primaryAlt,
                  },
                }}
                media={<Icon class="fas-file-contract" />}
                info={lang.productDocuments}
                title={productDocumentHook.productDocument?.displayInfo?.title}
                subtitle={productDocumentHook.productDocument?.documentCategory}
              />
              {productDocumentHook.productDocument && (
                <Box>
                  <UploadDocumentTranslationButton
                    documentId={productDocumentHook.productDocument.id}
                    documentType="ProductDocument"
                    translatedDocuments={productDocumentHook.translatedDocumentList}
                    onFinish={productDocumentHook.reloadTranslatedDocs}
                  />
                </Box>
              )}
            </Box>
            <div
              style={{
                height: 'fit-content',
                fontSize: Theme.FontSize.M,
                display: 'flex',
                marginLeft: Theme.Size.pixel(10),
              }}
              children={
                <>
                  <Button
                    key={'dealers-tab'}
                    render={lang.dealers}
                    onClick={productDocumentHook.setSelectedTab}
                    data={ProductDocumentTabs.DEALERS}
                    active={productDocumentHook.selectedTab === ProductDocumentTabs.DEALERS}
                    class={ThemeButtonTab}
                  />
                  <Button
                    key={'dealer-groups-tab'}
                    render={lang.dealerGroup}
                    onClick={productDocumentHook.setSelectedTab}
                    data={ProductDocumentTabs.DEALER_GROUPS}
                    active={productDocumentHook.selectedTab === ProductDocumentTabs.DEALER_GROUPS}
                    class={ThemeButtonTab}
                  />
                </>
              }
            />
          </>
        }
        subHeader={
          <>
            <b style={{ marginRight: 'auto' }} children={lang.productDocuments} />
            <Button
              class={isMobile ? ThemeButtonFloating : ThemeButtonCircle}
              active
              media={<Icon class="fas-plus" />}
              render={!isMobile && lang.add}
              onClick={setView}
              data={ProductDealerGroupDocumentsAction.ADD}
            />
            &nbsp;
            {selectedItems?.length || selectedTranslatedItems?.length ? (
              <>
                <Button
                  class={isMobile ? ThemeButtonFloating : ThemeButtonCircle}
                  active
                  media={<Icon class="fas-trash" />}
                  render={!isMobile && lang.delete}
                  onClick={setView}
                  data={ProductDealerGroupDocumentsAction.DELETE}
                />
                {currentView === ProductDealerGroupDocumentsAction.DELETE && (
                  <Confirm
                    onConfirm={() => {
                      setView(undefined);
                      handleDeleteDealerGroupDocumentSubmit();
                    }}
                    onClose={() => setView(undefined)}
                    actionName={replaceStringTemplateWithValues(lang.productDealerDocumentDeleteSubmitMessage, {
                      numberOfDocuments: (selectedItems.length ?? 0 + selectedTranslatedItems.length ?? 0).toString(),
                    })}
                    confirmationType="nextButton"
                    subcomponents={ThemeConfirmDefault}
                  />
                )}
              </>
            ) : null}
            &nbsp;
            {dealerGroupDocumentList.count > 0 && (
              <>
                <Button
                  class={{ button: ThemeButtonCircle }}
                  media={
                    selectedItems?.length === dealerGroupDocumentList.count
                      ? 'fas-check-square'
                      : selectedItems?.length !== dealerGroupDocumentList.count
                        ? 'fas-minus-square'
                        : 'far-square'
                  }
                  data={!(selectedItems?.length === dealerGroupDocumentList.count)}
                  onClick={toggleSelectAll}
                  active={selectedItems?.length === dealerGroupDocumentList.count}
                  htmlElementProps={{ title: `${lang.selectAll} (EN)` }}
                  render={`${lang.selectAll} (EN)`}
                />
                &nbsp;
              </>
            )}
            {translatedDocumentList.length > 0 && (
              <>
                <Button
                  class={{ button: ThemeButtonCircle }}
                  media={
                    selectedTranslatedItems?.length === translatedDocumentList.length
                      ? 'fas-check-square'
                      : selectedTranslatedItems?.length !== translatedDocumentList.length
                        ? 'fas-minus-square'
                        : 'far-square'
                  }
                  data={!(selectedTranslatedItems?.length === translatedDocumentList.length)}
                  onClick={toggleSelectAllTranslations}
                  active={selectedTranslatedItems?.length === translatedDocumentList.length}
                  htmlElementProps={{ title: `${lang.selectAll} (FR)` }}
                  render={`${lang.selectAll} (FR)`}
                />
                &nbsp;
              </>
            )}
            <ProductDealerGroupDocumentFilters />
          </>
        }
        render={
          <>
            <DealerGroupDocumentList />
            {views.add && <ProductDealerGroupDocumentAdd />}
          </>
        }
        footer={
          <Pagination
            class={ThemePaginationRounded}
            count={dealerGroupDocumentList.count}
            offset={dealerGroupDocumentList.offset}
            limit={dealerGroupDocumentList.limit}
            onLimitChange={(limit) =>
              dealerGroupDocumentList.setDealerGroupDocumentFilters((old) => ({
                ...old,
                limit,
                offset: 0,
              }))
            }
            onOffsetChange={(offset) =>
              dealerGroupDocumentList.setDealerGroupDocumentFilters((old) => ({ ...old, offset }))
            }
          />
        }
      />
    </>
  );
}
